import { type LucideIcon } from 'lucide-react';

import { cn } from '../../utils/utils';
import { Card } from '../card';

export interface StatTileProps {
  icon: LucideIcon;
  /** The metric's name. Sits beside the icon, above the number. */
  label: string;
  value: number | string;
  /** A unit suffix — `hrs`, `min`, `%`. Rendered smaller and muted. */
  unit?: string;
  className?: string;
}

/**
 * One number with its name: an icon-and-label row above a large figure.
 *
 * `elevation='none'` because these ship in a grid of four. A per-card shadow in
 * a tight grid reads as noise rather than lift — the card edges alone already
 * separate them.
 *
 * `tabular-nums` on the value is load-bearing, not typographic polish. These
 * tiles sit in a row and their numbers change as the user switches period; with
 * proportional figures the values jitter horizontally on every change, because
 * a `1` is narrower than a `7` in Inter's default set.
 *
 * The unit is a `<span>` inside the same block as the number rather than its
 * own element, so `4` and `hrs` stay on one baseline and wrap together.
 */
export function StatTile({
  icon: Icon,
  label,
  value,
  unit,
  className,
}: StatTileProps) {
  return (
    <Card elevation='none' className={cn('px-4 py-3', className)}>
      <div className='mb-1.5 flex items-center gap-1.5'>
        <Icon className='text-muted-foreground h-3.5 w-3.5' aria-hidden />
        <span className='text-muted-foreground text-xs font-medium'>
          {label}
        </span>
      </div>
      <p className='text-foreground text-xl font-bold tabular-nums'>
        {value}
        {unit && (
          <span className='text-muted-foreground ml-0.5 text-sm font-normal'>
            {unit}
          </span>
        )}
      </p>
    </Card>
  );
}
